import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Shield, Lock, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { authAPI } from '../../services/api';
import '../../styles/vds-auth.css';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing reset link. Please request a new one.');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setLoading(true);
    
    try {
      await authAPI.resetPassword(token, password);
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to reset password';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="vds-auth-page">
      <div className="vds-auth-container">
        <div className="vds-auth-header">
          <Link to="/" className="vds-auth-logo">
            <div className="vds-auth-logo-mark">
              <Shield className="h-5 w-5" />
            </div>
            <span className="vds-auth-logo-text">VETT</span>
          </Link>
          <h1 className="vds-auth-title">
            {success ? 'Password reset!' : 'Set new password'}
          </h1>
          <p className="vds-auth-subtitle">
            {success ? 'You can now sign in with your new password' : 'Enter your new password below'}
          </p>
        </div>

        <div className="vds-auth-card">
          {error && !success ? (
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <div style={{ 
                width: '64px', 
                height: '64px', 
                borderRadius: '16px', 
                background: 'rgba(229, 72, 77, 0.1)', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <AlertCircle className="w-8 h-8" style={{ color: '#E5484D' }} />
              </div>
              <h2 style={{ 
                fontFamily: "'Bricolage Grotesque', sans-serif",
                fontSize: '20px',
                fontWeight: 700,
                color: '#0A0F1E',
                marginBottom: '8px'
              }}>
                Link expired or invalid
              </h2>
              <p style={{ 
                fontFamily: "'Bricolage Grotesque', sans-serif",
                fontSize: '14px',
                fontWeight: 600,
                color: '#5B6472',
                marginBottom: '24px'
              }}>
                {error}
              </p>
              <Link to="/forgot-password">
                <button className="vds-btn-auth-primary">
                  Request new link
                </button>
              </Link>
            </div>
          ) : success ? (
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <div style={{ 
                width: '64px', 
                height: '64px', 
                borderRadius: '16px', 
                background: 'rgba(0, 201, 167, 0.1)', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <CheckCircle className="w-8 h-8" style={{ color: '#00C9A7' }} />
              </div>
              <h2 style={{ 
                fontFamily: "'Bricolage Grotesque', sans-serif",
                fontSize: '20px',
                fontWeight: 700,
                color: '#0A0F1E',
                marginBottom: '8px'
              }}>
                Password updated!
              </h2>
              <p style={{ 
                fontFamily: "'Bricolage Grotesque', sans-serif",
                fontSize: '14px',
                fontWeight: 600,
                color: '#5B6472',
                marginBottom: '24px'
              }}>
                Your password has been changed successfully.
              </p>
              <button
                onClick={() => navigate('/login')}
                className="vds-btn-auth-primary"
                data-testid="go-to-login-btn"
              >
                SIGN IN NOW
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="vds-auth-form">
              <div className="vds-form-group">
                <label className="vds-form-label" htmlFor="password">New password</label>
                <div className="vds-input-wrapper">
                  <Lock className="vds-input-icon h-5 w-5" />
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="vds-input"
                    required
                    minLength={6}
                    data-testid="new-password-input"
                  />
                </div>
                <p style={{ 
                  fontFamily: "'DM Mono', monospace",
                  fontSize: '11px',
                  color: '#5B6472',
                  marginTop: '4px'
                }}>
                  Must be at least 6 characters
                </p>
              </div>

              <div className="vds-form-group">
                <label className="vds-form-label" htmlFor="confirmPassword">Confirm password</label>
                <div className="vds-input-wrapper">
                  <Lock className="vds-input-icon h-5 w-5" />
                  <input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    className="vds-input"
                    required
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="vds-btn-auth-primary"
                data-testid="reset-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Resetting...
                  </>
                ) : (
                  'RESET PASSWORD'
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
